'use client';

import { useState } from 'react';
import { Button } from '@/components';
import type { EmailCaptureSection as EmailCaptureSectionType } from '@shared/lib/page-sections/types';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

export default function EmailCaptureSection({ section }: { section: EmailCaptureSectionType }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/email-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: section.source || 'page' }),
      });
      if (!res.ok) throw new Error('Subscribe failed');
      setDone(true);
      setEmail('');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="w-full px-4 py-10 md:py-14">
      <div className="mx-auto max-w-2xl rounded-lg border border-slate-200 bg-slate-50 p-6 text-center md:p-10">
        {section.heading ? (
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {section.heading}
          </h2>
        ) : null}
        {section.description ? (
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">{section.description}</p>
        ) : null}

        {done ? (
          <p className="mt-4 text-sm font-semibold text-emerald-700">Thanks, you&apos;re on the list.</p>
        ) : (
          <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-2 sm:flex-row">
            <label htmlFor={`${section.id}-email`} className="sr-only">Email address</label>
            <input
              id={`${section.id}-email`}
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <Button
              type="submit"
              variant="ghost"
              size="md"
              disabled={submitting}
              className="rounded-md px-5 py-2"
            >
              {submitting ? 'Subscribing…' : section.buttonLabel || 'Subscribe'}
            </Button>
          </form>
        )}
        {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
      </div>
    </section>
  );
}
